import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface Application {
  id: number;
  job_id: number;
  worker_id: number;
  bid_amount: number;
  cover_letter: string;
  estimated_duration: string;
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn';
  created_at: string;
  job_title?: string;
  job_budget?: number;
  employer_name?: string;
}

export interface ApplicationsState {
  applications: Application[];
  jobApplications: Application[];
  loading: boolean;
  error: string | null;
}

const initialState: ApplicationsState = {
  applications: [],
  jobApplications: [],
  loading: false,
  error: null,
};

export const applyToJob = createAsyncThunk(
  'applications/applyToJob',
  async (
    data: {
      jobId: number;
      bidAmount: number;
      coverLetter: string;
      estimatedDuration?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/jobs/${data.jobId}/apply`, {
        bidAmount: data.bidAmount,
        coverLetter: data.coverLetter,
        estimatedDuration: data.estimatedDuration,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to apply');
      }
      return rejectWithValue('Failed to apply');
    }
  }
);

export const fetchJobApplications = createAsyncThunk(
  'applications/fetchJobApplications',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/jobs/${jobId}/applications`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch applications');
      }
      return rejectWithValue('Failed to fetch applications');
    }
  }
);

const applicationsSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(applyToJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyToJob.fulfilled, (state, action) => {
        state.loading = false;
        state.applications.push(action.payload);
      })
      .addCase(applyToJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchJobApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobApplications.fulfilled, (state, action) => {
        state.loading = false;
        state.jobApplications = action.payload;
      })
      .addCase(fetchJobApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default applicationsSlice.reducer;
